import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

// What the overlay actually found (TGCLOUD-1670).
//
// An HTML row inside the family card, under the tool strip. Not a floating bar
// over the canvas: the canvas is the exported artefact and everything drawn on
// it has to be justified to a clinical reader, and a bar that floats over a
// pedigree covers the very nodes it is describing.
//
// The counts are the honesty surface. A gap-spotting tool that says "12" and
// silently means "12 of the ones I could read" is worse than no tool, so hidden,
// unresolved and indeterminate are shown beside the match count rather than
// folded into it, each with a tooltip saying what it means.

const Swatch = ({ colour, dashed }) => (
  <Box
    component="span"
    data-ring-style={dashed ? 'dashed' : 'solid'}
    sx={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      border: `2px ${dashed ? 'dashed' : 'solid'} ${colour}`,
      flex: '0 0 auto'
    }}
  />
);

const Count = ({ children, title }) => (
  <Tooltip title={title}>
    <Box
      component="span"
      sx={{ color: 'text.secondary', cursor: 'help', whiteSpace: 'nowrap' }}
    >
      {children}
    </Box>
  </Tooltip>
);

const SlotRow = ({
  slot,
  index,
  cursor,
  fmtDate,
  onStep,
  onSelect,
  onRefresh,
  onRemove,
  onOpenHidden
}) => {
  const { preset, results, colour } = slot;
  if (!results) return null;

  const onCanvas = results.matches.filter((m) => m.onCanvas);
  const position = onCanvas.length ? (cursor % onCanvas.length) + 1 : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        fontSize: 12,
        py: 0.25
      }}
    >
      <Swatch colour={colour} dashed={index === 1} />
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        {preset?.title || results.title}
      </Typography>
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {results.count} of {results.rosterSize}
      </Box>

      {results.hiddenCount > 0 && (
        <Tooltip title="Matched members that are hidden on this chart. Click to open the hidden members panel.">
          <Button
            size="small"
            onClick={onOpenHidden}
            sx={{ fontSize: 12, minWidth: 0, p: 0.25, textTransform: 'none' }}
          >
            {results.hiddenCount} hidden
          </Button>
        </Tooltip>
      )}
      {results.unresolvedCount > 0 && (
        <Count
          title={`${results.unresolvedCount} records could not be placed on a person`}
        >
          {results.unresolvedCount} unresolved
        </Count>
      )}
      {results.indeterminateCount > 0 && (
        <Count
          title={`${results.indeterminateCount} people have private records this overlay cannot read`}
        >
          {results.indeterminateCount} indeterminate
        </Count>
      )}

      {onCanvas.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            aria-label="Previous match"
            onClick={() => onStep(-1)}
          >
            <ChevronLeftIcon fontSize="inherit" />
          </IconButton>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {position} / {onCanvas.length}
          </Box>
          <IconButton
            size="small"
            aria-label="Next match"
            onClick={() => onStep(1)}
          >
            <ChevronRightIcon fontSize="inherit" />
          </IconButton>
        </Box>
      )}

      {results.count > 0 && (
        <Button
          size="small"
          onClick={onSelect}
          sx={{ fontSize: 12, minWidth: 0, p: 0.25, textTransform: 'none' }}
        >
          Select
        </Button>
      )}
      <Tooltip title="Re-read the records">
        <IconButton size="small" aria-label="Refresh" onClick={onRefresh}>
          <RefreshIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Turn this overlay off">
        <IconButton size="small" aria-label="Remove overlay" onClick={onRemove}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Box
        component="span"
        sx={{ ml: 'auto', color: 'text.disabled', whiteSpace: 'nowrap' }}
      >
        as of {fmtDate ? fmtDate(results.asOf) : results.asOf}
      </Box>
    </Box>
  );
};

const DataOverlayResultsBar = ({
  slots = [],
  cursors = [],
  // `results.asOf` is a machine-readable 'YYYY-MM-DD' stamp, which is right for
  // comparison and for tests and wrong to put in front of a clinician: every
  // other date in this app honours Settings > Localisation. Rendering the raw
  // stamp showed "2026-09-04" on a deployment configured for DD/MM/YYYY.
  fmtDate,
  onStep,
  onSelect,
  onRefresh,
  onRemove,
  onOpenHidden
}) => {
  const active = slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => slot && slot.enabled && slot.results);

  if (!active.length) return null;

  return (
    <Box
      data-va-anchor="pedigree.overlays.results"
      sx={{
        pointerEvents: 'auto',
        px: '10px',
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      {active.map(({ slot, index }) => (
        <SlotRow
          key={`${slot.presetId}-${index}`}
          slot={slot}
          index={index}
          cursor={cursors[index] || 0}
          fmtDate={fmtDate}
          onStep={(delta) => onStep(index, delta)}
          onSelect={() => onSelect(index)}
          onRefresh={onRefresh}
          onRemove={() => onRemove(index)}
          onOpenHidden={onOpenHidden}
        />
      ))}
    </Box>
  );
};

export default DataOverlayResultsBar;
